import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";

// Short, scannable beliefs. This section used to be three long myth-vs-truth
// paragraphs; nobody reads that far down a page they just landed on.
const BELIEFS = [
  {
    claim: "One to one beats everything else.",
    line: "No cohort, no playlist. Someone who remembers where you got stuck last week.",
  },
  {
    claim: "Jobs ask for more than you think.",
    line: "Interviews, and the work after them, go deeper than any crash course goes.",
  },
  {
    claim: "Building is easy now. Running it isn't.",
    line: "AI will write you a working product in a day. Deploying, scaling and maintaining it is where real knowledge starts.",
  },
  {
    claim: "Enough theory, enough practice.",
    line: "Built so you come out genuinely capable — not overnight, but sooner than you'd expect.",
  },
];

export const Honest = () => {
  const reduced = useReducedMotion();

  return (
    <Box
      component="section"
      aria-labelledby="honest-heading"
      sx={{ py: { xs: 8, md: 13 } }}
    >
      <Container maxWidth="lg">
        <motion.div variants={stagger(0.09)} {...revealProps(reduced)}>
          <motion.div variants={fadeUp}>
            <Typography
              component="p"
              sx={{
                color: palette.primary,
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              Before anything else
            </Typography>
          </motion.div>

          <motion.div variants={fadeUp}>
            <Typography
              component="h2"
              id="honest-heading"
              sx={{
                color: palette.text,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                lineHeight: 1.12,
                fontSize: { xs: 30, sm: 38, md: 45 },
                mb: { xs: 5, md: 7 },
              }}
            >
              What we believe
            </Typography>
          </motion.div>

          <Grid container spacing={{ xs: 4, md: 4 }}>
            {BELIEFS.map((b) => (
              <Grid item xs={12} sm={6} md={3} key={b.claim}>
                <motion.div variants={fadeUp}>
                  <Box sx={{ borderTop: `2px solid ${palette.primary}`, pt: 2.5, height: "100%" }}>
                    <Typography
                      component="h3"
                      sx={{
                        color: palette.text,
                        fontWeight: 700,
                        fontSize: { xs: 18, md: 19 },
                        letterSpacing: "-0.02em",
                        lineHeight: 1.3,
                        mb: 1.25,
                      }}
                    >
                      {b.claim}
                    </Typography>
                    <Typography
                      component="p"
                      sx={{ color: palette.textMuted, fontSize: 15, lineHeight: 1.6 }}
                    >
                      {b.line}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <motion.div variants={fadeUp}>
            <Typography
              component="p"
              sx={{
                color: palette.text,
                fontSize: { xs: 18, md: 21 },
                lineHeight: 1.55,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                maxWidth: 680,
                mt: { xs: 5, md: 7 },
              }}
            >
              None of it is beyond you. It just takes effort, time, and someone
              who has already made the mistakes.
            </Typography>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Honest;
